import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { styled } from '@mui/material/styles';
import {
  ServicePlan,
  ServicePlanServiceArea,
} from '../../../data/models/client-program.model';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { TransportationBenefit } from '../../../data/models/transportation-benefit.model';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { ClientPurposeCode } from '../../../data/models/purpose-code-model.model';

const PREFIX = 'ServicePlanList';

const classes = {
  container: `${PREFIX}-container`,
  panelTools: `${PREFIX}-panelTools`,
  panelTitle: `${PREFIX}-panelTitle`,
  actions: `${PREFIX}-actions`,
  summary: `${PREFIX}-summary`,
  purposeCodes: `${PREFIX}-purposeCodes`,
  highlight: `${PREFIX}-highlight`,
  chip: `${PREFIX}-chip`,
  pcchip: `${PREFIX}-pcchip`,
  sachip: `${PREFIX}-sachip`,
  tbchip: `${PREFIX}-tbchip`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    gap: theme.spacing(1),
  },

  [`& .${classes.panelTools}`]: {
    width: '100%',
  },

  [`& .${classes.panelTitle}`]: {
    flexGrow: 1,
    fontWeight: 700,
  },

  [`& .${classes.actions}`]: {
    whiteSpace: 'nowrap',
  },

  [`& .${classes.summary}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },

  [`& .${classes.purposeCodes}`]: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    pointerEvents: 'none',
  },

  [`& .${classes.highlight}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },

  [`& .${classes.chip}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    color: theme.palette.primary.dark,
    '& .MuiChip-deleteIcon': {
      color: theme.palette.primary.dark,
      pointerEvents: 'all',
    },
  },

  [`& .${classes.pcchip}`]: {
    backgroundColor: '#bce1f5',
    'span.MuiChip-label': {
      color: `${theme.palette.primary.dark}`,
    },
  },

  [`& .${classes.sachip}`]: {
    backgroundColor: '#bcf5e1',
    'span.MuiChip-label': {
      color: `${theme.palette.primary.dark}`,
    },
  },

  [`& .${classes.tbchip}`]: {
    backgroundColor: '#f6cff6',
    'span.MuiChip-label': {
      color: `${theme.palette.primary.dark}`,
    },
  },
}));

interface ServicePlanListProps {
  servicePlans: ServicePlan[];
  continuationToken?: string;
  fetchMoreServicePlans: (token: string) => void;
  purposeCodes: ClientPurposeCode[];
  serviceAreas: ServicePlanServiceArea[];
  transportationBenefits: TransportationBenefit[];
  servicePlanSelections: { [id: string]: boolean };
  setServicePlanSelections: (sels: { [id: string]: boolean }) => void;
  onEdit: (evt: any, servicePlan: ServicePlan) => void;
  onActivate: (evt: any, servicePlan: ServicePlan) => void;
  onDeactivate: (evt: any, servicePlan: ServicePlan) => void;
  onDelete: (evt: any, servicePlan: ServicePlan) => void;
  onUnassignPurposeCode: (
    servicePlan: ServicePlan,
    purposeCode: ClientPurposeCode,
  ) => void;
  onUnassignServiceArea: (
    servicePlan: ServicePlan,
    serviceArea: ServicePlanServiceArea,
  ) => void;
  onUnassignTransportationBenefit: (
    servicePlan: ServicePlan,
    transportationBenefit: TransportationBenefit,
  ) => void;
  onStartDrag: (servicePlan: ServicePlan) => void;
  onEndDrag: (servicePlan: ServicePlan) => void;
  dropServicePlanUUIDs: string[];
}

export default function ServicePlanList({
  servicePlans,
  continuationToken,
  fetchMoreServicePlans,
  purposeCodes,
  serviceAreas,
  transportationBenefits,
  servicePlanSelections,
  setServicePlanSelections,
  onEdit,
  onActivate,
  onDeactivate,
  onDelete,
  onUnassignPurposeCode,
  onUnassignServiceArea,
  onUnassignTransportationBenefit,
  onStartDrag,
  onEndDrag,
  dropServicePlanUUIDs,
}: ServicePlanListProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();

  const isSuperReaderUser = useIsSuperReaderUser();

  const handleActiveChange = (evt: any, servicePlan: ServicePlan) => {
    if (evt.target.checked) {
      onActivate(evt, servicePlan);
    } else {
      onDeactivate(evt, servicePlan);
    }
  };

  const toggleSelection = (evt: any, servicePlan: ServicePlan) => {
    evt.preventDefault();
    evt.stopPropagation();
    servicePlanSelections[servicePlan.servicePlanUUID!] =
      !servicePlanSelections[servicePlan.servicePlanUUID!];
    setServicePlanSelections({ ...servicePlanSelections });
  };

  return (
    <StyledBox className={classes.container}>
      {servicePlans.map((servicePlan, idx) => {
        const className = dropServicePlanUUIDs.includes(
          servicePlan.servicePlanUUID || '',
        )
          ? classes.highlight
          : '';
        return (
          <Accordion key={servicePlan.servicePlanUUID} className={className}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                className={classes.panelTools}
                draggable={servicePlan.state === 'active'}
                onDragStart={() => onStartDrag(servicePlan)}
                onDragEnd={() => onEndDrag(servicePlan)}>
                <Box className={classes.summary}>
                  {!isSuperReaderUser && (
                    <Checkbox
                      color="primary"
                      onClick={(evt) => toggleSelection(evt, servicePlan)}
                      checked={
                        !!servicePlanSelections[servicePlan.servicePlanUUID!]
                      }
                    />
                  )}
                  <Typography className={classes.panelTitle}>
                    {servicePlan.name}
                  </Typography>
                  <Box className={classes.actions}>
                    {!isSuperReaderUser && (
                      <Tooltip title={t('client.editServicePlan')}>
                        <span>
                          <IconButton
                            data-testid="edit-contact"
                            onClick={(evt) => onEdit(evt, servicePlan)}
                            color="primary"
                            size="large">
                            <EditOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {!isSuperReaderUser && (
                      <Tooltip title={t('client.deleteServicePlan')}>
                        <span>
                          <IconButton
                            data-testid="delete-service-plan"
                            onClick={(evt) => onDelete(evt, servicePlan)}
                            color="error"
                            size="large"
                            disabled={servicePlan.state === 'active'}>
                            <DeleteForeverIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {!isSuperReaderUser && (
                      <Tooltip
                        title={
                          servicePlan.state === 'active'
                            ? t('client.active')
                            : t('client.inactive')
                        }>
                        <Switch
                          checked={servicePlan.state === 'active'}
                          onChange={(evt) =>
                            handleActiveChange(evt, servicePlan)
                          }
                          onClick={(evt) => evt.stopPropagation()}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box className={classes.purposeCodes}>
                {servicePlan.purposeCodeUUIDs?.map((purposeCodeUUID) => {
                  const purposeCode = purposeCodes.find(
                    (pc) => pc.purposeCodeUUID === purposeCodeUUID,
                  );
                  return purposeCode ? (
                    <Chip
                      color="primary"
                      className={`${classes.chip} ${classes.pcchip}`}
                      label={purposeCode.name}
                      key={purposeCode.purposeCodeUUID}
                      onDelete={
                        !isSuperReaderUser
                          ? () =>
                              onUnassignPurposeCode(servicePlan, purposeCode)
                          : undefined
                      }
                    />
                  ) : null;
                })}
                {servicePlan.serviceAreaUUIDs?.map((serviceAreaUUID) => {
                  const serviceArea = serviceAreas.find(
                    (sa) => sa.serviceAreaUUID === serviceAreaUUID,
                  );
                  return serviceArea ? (
                    <Chip
                      color="primary"
                      className={`${classes.chip} ${classes.sachip}`}
                      label={serviceArea.name}
                      key={serviceArea.serviceAreaUUID}
                      onDelete={
                        !isSuperReaderUser
                          ? () =>
                              onUnassignServiceArea(servicePlan, serviceArea)
                          : undefined
                      }
                    />
                  ) : null;
                })}
                {servicePlan.transportationBenefitUUIDs?.map(
                  (transportationBenefitUUID) => {
                    const transportationBenefit = transportationBenefits.find(
                      (sa) =>
                        sa.transportationBenefitUUID ===
                        transportationBenefitUUID,
                    );
                    return transportationBenefit ? (
                      <Chip
                        color="primary"
                        className={`${classes.chip} ${classes.tbchip}`}
                        label={transportationBenefit.name}
                        key={transportationBenefit.transportationBenefitUUID}
                        onDelete={
                          !isSuperReaderUser
                            ? () =>
                                onUnassignTransportationBenefit(
                                  servicePlan,
                                  transportationBenefit,
                                )
                            : undefined
                        }
                      />
                    ) : null;
                  },
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
      {continuationToken && (
        <Button
          variant="text"
          color="primary"
          onClick={() => fetchMoreServicePlans(continuationToken)}>
          {t('client.loadMore')}
        </Button>
      )}
    </StyledBox>
  );
}
